import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { Tag, UserTagRow } from "services/tags/type"
import { userTagsService } from "services/tags/user-tags.service"

type Props = {
  userId: string
  refreshKey?: number
}

export const UserTagsSection: FC<Props> = ({ userId, refreshKey }) => {
  const [loading, setLoading] = useState(false)
  const [tagsLoading, setTagsLoading] = useState(false)

  const [userTags, setUserTags] = useState<UserTagRow[]>([])
  const [availableTags, setAvailableTags] = useState<Tag[]>([])
  const [selectedTagId, setSelectedTagId] = useState<string>("")

  const load = useCallback(async () => {
    if (!userId) return
    try {
      setLoading(true)
      const [assigned, available] = await Promise.all([userTagsService.getUserTags(userId), userTagsService.getAvailableTags(userId)])
      setUserTags(assigned ?? [])
      setAvailableTags(available ?? [])
      setSelectedTagId("")
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    load()
  }, [load, refreshKey])

  const assignedTagIds = useMemo(() => new Set(userTags.map((r) => r.tag.id)), [userTags])

  const handleAssign = useCallback(async () => {
    if (!selectedTagId) return
    if (assignedTagIds.has(selectedTagId)) return

    setTagsLoading(true)
    try {
      const res = await userTagsService.assignTag(userId, selectedTagId)
      if (res?.created) toast.success("Tag assigned")
      else toast.info("Tag already assigned")
      await load()
    } finally {
      setTagsLoading(false)
    }
  }, [selectedTagId, userId, assignedTagIds, load])

  const handleRemove = useCallback(
    async (tagId: string) => {
      setTagsLoading(true)
      try {
        const res = await userTagsService.removeTag(userId, tagId)
        if (res?.removed) toast.success("Tag removed")
        else toast.info("Tag not found")
        await load()
      } finally {
        setTagsLoading(false)
      }
    },
    [userId, load]
  )

  return (
    <div className="w-full rounded-xl border border-[rgba(139,92,246,0.25)] bg-white dark:bg-[#0C0C1A] p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="text-base font-semibold text-[rgba(6,7,21,1)] dark:text-white">Tags</div>

        <button className={clsx("btn btn-sm", { "btn-disabled": loading })} onClick={load}>
          Refresh
        </button>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {loading ? (
          <span className="loading loading-spinner loading-md" />
        ) : userTags.length === 0 ? (
          <div className="text-sm opacity-70">No tags assigned</div>
        ) : (
          userTags.map((row) => (
            <div
              key={row.id}
              className="flex items-center gap-2 rounded-full px-3 py-1 text-sm border border-[rgba(255,255,255,0.1)]"
              style={{ backgroundColor: row.tag.color ?? "#64748B" }}
              title={row.tag.description ?? ""}
            >
              <span className="text-white">{row.tag.name}</span>
              <button
                className={clsx("text-white/90 hover:text-white", { "pointer-events-none opacity-60": tagsLoading })}
                onClick={() => handleRemove(row.tag.id)}
              >
                ✕
              </button>
            </div>
          ))
        )}
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2">
        <select
          className="select select-bordered w-full"
          value={selectedTagId}
          onChange={(e) => setSelectedTagId(e.target.value)}
          disabled={loading || tagsLoading}
        >
          <option value="">Select tag to add...</option>
          {availableTags.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
              {t.category ? ` (${t.category})` : ""}
            </option>
          ))}
        </select>

        <button className={clsx("btn btn-primary", { "btn-disabled": !selectedTagId || tagsLoading || loading })} onClick={handleAssign}>
          {tagsLoading ? <span className="loading loading-spinner loading-sm" /> : "Add"}
        </button>
      </div>

      {/* History */}
    </div>
  )
}
